import { useEffect, useState } from "react";
import axios from "axios";
import { Lock, Download, MapPin, Mail, RefreshCw, LogOut, Globe, Smartphone, ClipboardCopy, MessageCircle, CalendarDays } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY = "insights_key";

const fmt = (iso) =>
  new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

const place = (r) => [r.city, r.region, r.country].filter(Boolean).join(", ") || "Unknown location";

const Stat = ({ label, value, testid }) => (
  <div data-testid={testid} className="border-t border-champagne/40 pt-5">
    <p className="text-xs uppercase tracking-[0.25em] text-champagne-dark">{label}</p>
    <p className="mt-3 font-display text-5xl md:text-6xl text-emerald leading-none">{value}</p>
  </div>
);

const Gate = ({ onUnlock }) => {
  const [code, setCode] = useState("");
  return (
    <form
      data-testid="insights-gate"
      onSubmit={(e) => { e.preventDefault(); onUnlock(code); }}
      className="min-h-screen bg-emerald-dark grain-overlay flex items-center justify-center px-6"
    >
      <div className="w-full max-w-sm">
        <Lock className="text-champagne" size={28} />
        <h1 className="mt-6 font-display font-light text-4xl text-ivory">Portfolio Insights</h1>
        <p className="mt-2 text-ivory/60 text-sm">Private dashboard — enter your passcode.</p>
        <input
          data-testid="insights-passcode-input"
          type="password"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="Passcode"
          className="mt-8 w-full bg-transparent border-b border-ivory/25 py-3 text-ivory placeholder:text-ivory/40 focus:border-champagne focus:outline-none"
        />
        <button
          data-testid="insights-unlock-btn"
          type="submit"
          className="mt-8 w-full bg-champagne text-emerald py-4 uppercase tracking-[0.15em] text-sm hover:bg-champagne-light transition-colors"
        >
          Unlock
        </button>
      </div>
    </form>
  );
};

export default function Insights() {
  const [key, setKey] = useState(() => sessionStorage.getItem(KEY) || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (k) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/insights`, { headers: { "X-Insights-Key": k } });
      setData(r.data);
      sessionStorage.setItem(KEY, k);
      setKey(k);
    } catch (e) {
      toast.error(e.response?.status === 401 ? "Incorrect passcode." : "Could not load insights.");
      sessionStorage.removeItem(KEY);
      setKey("");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (key) load(key); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!key || !data) return <Gate onUnlock={load} />;

  const { downloads, by_region, recent_downloads, visits, visits_by_region, visits_by_source, visits_by_device, visits_daily = [], enquiries } = data;

  const digest = () => {
    const topRegions = visits_by_region.slice(0, 3).map((r) => `${r.city || r.region || r.country || "Unknown"} (${r.count})`).join(", ") || "—";
    const topSource = visits_by_source[0] ? `${visits_by_source[0].source || "direct"} (${visits_by_source[0].count})` : "—";
    const d7 = new Date(); d7.setDate(d7.getDate() - 7);
    const enq7 = enquiries.recent.filter((e) => new Date(e.created_at) >= d7).length;
    return [
      `Weekly Digest — Dr. Priya H. Mishra`,
      `${d7.toLocaleDateString("en-IN", { day: "2-digit", month: "short" })} – ${new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}`,
      ``,
      `Site visits: ${visits.last7} (total ${visits.total})`,
      `Portfolio downloads: ${downloads.last7} (total ${downloads.total})`,
      `Enquiries: ${enq7} (total ${enquiries.total})`,
      `Top regions: ${topRegions}`,
      `Top source: ${topSource}`,
    ].join("\n");
  };

  const copyDigest = async () => {
    try { await navigator.clipboard.writeText(digest()); toast.success("Weekly digest copied."); }
    catch (e) { toast.error("Copy not available in this browser."); }
  };

  const List = ({ testid, rows, label, empty }) => (
    <ul data-testid={testid} className="mt-6 divide-y divide-emerald/10">
      {rows.length === 0 && <li className="py-4 text-sm text-emerald/50">{empty}</li>}
      {rows.map((r, i) => (
        <li key={i} className="py-4 flex items-center justify-between gap-4">
          <span className="text-sm text-emerald capitalize">{label(r)}</span>
          <span className="font-display text-xl text-emerald">{r.count}</span>
        </li>
      ))}
    </ul>
  );

  return (
    <main data-testid="insights-dashboard" className="min-h-screen bg-ivory px-6 md:px-12 py-14 max-w-[1200px] mx-auto">
      <header className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="text-champagne-dark text-xs uppercase tracking-[0.3em]">Private · Dr. Priya H. Mishra</p>
          <h1 className="mt-4 font-display font-light text-4xl md:text-5xl text-emerald">Portfolio Insights</h1>
        </div>
        <div className="flex flex-wrap gap-3">
          <button data-testid="digest-copy-btn" onClick={copyDigest}
            className="inline-flex items-center gap-2 bg-emerald text-ivory px-5 py-3 text-xs uppercase tracking-[0.15em] hover:bg-emerald-light transition-colors">
            <ClipboardCopy size={14} /> Copy weekly digest
          </button>
          <a data-testid="digest-whatsapp-btn" href={`https://wa.me/?text=${encodeURIComponent(digest())}`} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-2 border border-emerald px-5 py-3 text-xs uppercase tracking-[0.15em] text-emerald hover:bg-emerald hover:text-ivory transition-colors">
            <MessageCircle size={14} /> Share on WhatsApp
          </a>
          <button data-testid="insights-refresh-btn" onClick={() => load(key)} disabled={loading}
            className="inline-flex items-center gap-2 border border-champagne px-5 py-3 text-xs uppercase tracking-[0.15em] text-emerald hover:bg-champagne transition-colors">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <button data-testid="insights-logout-btn" onClick={() => { sessionStorage.removeItem(KEY); setKey(""); setData(null); }}
            className="inline-flex items-center gap-2 px-4 py-3 text-xs uppercase tracking-[0.15em] text-emerald/60 hover:text-emerald">
            <LogOut size={14} /> Lock
          </button>
        </div>
      </header>

      <section className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-8">
        <Stat testid="stat-visits" label="Site visits" value={visits.total} />
        <Stat testid="stat-visits-7d" label="Visits · 7 days" value={visits.last7} />
        <Stat testid="stat-visits-30d" label="Visits · 30 days" value={visits.last30} />
        <Stat testid="stat-enquiries" label="Enquiries" value={enquiries.total} />
      </section>

      <section className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-10">
        <div>
          <h2 className="flex items-center gap-3 text-emerald text-base md:text-lg font-display"><MapPin size={16} className="text-champagne-dark" /> Visitors by region</h2>
          <List testid="visits-region-list" rows={visits_by_region} label={place} empty="No visits recorded yet." />
        </div>
        <div>
          <h2 className="flex items-center gap-3 text-emerald text-base md:text-lg font-display"><Globe size={16} className="text-champagne-dark" /> Traffic source</h2>
          <List testid="visits-source-list" rows={visits_by_source} label={(r) => r.source || "direct"} empty="No visits recorded yet." />
        </div>
        <div>
          <h2 className="flex items-center gap-3 text-emerald text-base md:text-lg font-display"><Smartphone size={16} className="text-champagne-dark" /> Device</h2>
          <List testid="visits-device-list" rows={visits_by_device} label={(r) => r.device} empty="No visits recorded yet." />
        </div>
      </section>

      <section className="mt-16">
        <h2 className="flex items-center gap-3 text-emerald text-base md:text-lg font-display"><CalendarDays size={16} className="text-champagne-dark" /> Daily visits — last 14 days</h2>
        <div className="mt-6 overflow-x-auto">
          <table data-testid="daily-visits-table" className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-[0.2em] text-champagne-dark border-b border-emerald/15">
                <th className="py-3 pr-4 font-normal">Date</th>
                <th className="py-3 pr-4 font-normal text-right">Visits</th>
                <th className="py-3 pr-4 font-normal text-right">Mobile</th>
                <th className="py-3 font-normal text-right">Desktop</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-emerald/10">
              {visits_daily.length === 0 && (
                <tr><td colSpan={4} className="py-4 text-emerald/50">No visits recorded yet.</td></tr>
              )}
              {visits_daily.map((d) => (
                <tr key={d.date} className="text-emerald">
                  <td className="py-3 pr-4">{new Date(d.date).toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short" })}</td>
                  <td className="py-3 pr-4 text-right font-display text-lg">{d.total}</td>
                  <td className="py-3 pr-4 text-right text-emerald/70">{d.mobile}</td>
                  <td className="py-3 text-right text-emerald/70">{d.desktop}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8">
        <Stat testid="stat-total" label="Portfolio downloads" value={downloads.total} />
        <Stat testid="stat-7d" label="Downloads · 7 days" value={downloads.last7} />
        <Stat testid="stat-30d" label="Downloads · 30 days" value={downloads.last30} />
      </section>

      <section className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <h2 className="flex items-center gap-3 text-emerald text-base md:text-lg font-display"><MapPin size={16} className="text-champagne-dark" /> Downloads by region</h2>
          <ul data-testid="region-list" className="mt-6 divide-y divide-emerald/10">
            {by_region.length === 0 && <li className="py-4 text-sm text-emerald/50">No downloads recorded yet.</li>}
            {by_region.map((r, i) => (
              <li key={i} className="py-4 flex items-center justify-between gap-4">
                <span className="text-sm text-emerald">{place(r)}</span>
                <span className="font-display text-xl text-emerald">{r.count}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-7">
          <h2 className="flex items-center gap-3 text-emerald text-base md:text-lg font-display"><Download size={16} className="text-champagne-dark" /> Recent downloads</h2>
          <ul data-testid="recent-downloads" className="mt-6 divide-y divide-emerald/10">
            {recent_downloads.map((d) => (
              <li key={d.id} className="py-4 grid grid-cols-[1fr_auto] gap-4 text-sm">
                <span className="text-emerald">{place(d)}</span>
                <span className="text-emerald/50">{fmt(d.created_at)}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="mt-16">
        <h2 className="flex items-center gap-3 text-emerald text-base md:text-lg font-display"><Mail size={16} className="text-champagne-dark" /> Recent enquiries</h2>
        <ul data-testid="recent-enquiries" className="mt-6 divide-y divide-emerald/10">
          {enquiries.recent.length === 0 && <li className="py-4 text-sm text-emerald/50">No enquiries yet.</li>}
          {enquiries.recent.map((e) => (
            <li key={e.id} className="py-5">
              <div className="flex flex-wrap justify-between gap-3">
                <p className="text-emerald font-medium">{e.name} <span className="text-emerald/50 font-normal">· {e.email}{e.phone ? ` · ${e.phone}` : ""}</span></p>
                <span className="text-sm text-emerald/50">{fmt(e.created_at)}</span>
              </div>
              {e.subject && <p className="mt-1 text-xs uppercase tracking-[0.2em] text-champagne-dark">{e.subject}</p>}
              <p className="mt-2 text-sm text-emerald/70 leading-relaxed">{e.message}</p>
            </li>
          ))}
        </ul>
      </section>
    </main>
  );
}
